import * as vp from 'vopay';
import { Box, Title, Toast, Tooltip, Icon, CardTabs, useUpdateQueryParams } from 'vopay';
import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import {
  format,
  subMonths,
  startOfMonth,
  endOfMonth,
  subQuarters,
  startOfQuarter,
  endOfQuarter,
  subDays,
  startOfWeek,
  endOfWeek,
  compareAsc,
  parseISO,
} from 'date-fns';
import AccountingSoftware from '../../Intergrations/AccountingSoftware/AccountingSoftware';
import AccountingSystemSearchForm, {
  SearchParams,
  Currency,
  TimeRange,
  StatusFilter,
  PaymentChannel,
  DateFilterType,
} from './AccountingSystemSearchForm';
import AccountingSystemClosed from './AccountingSystemClosed/AccountingSystemClosed';
import AccountingSystemOpen from './AccountingSystemOpen/AccountingSystemOpen';
import useFetchRailzBusinessAccount from '@/hooks/useApi/useFetchRailzBusinessAccount';
import useFetchRailzBills from '@/hooks/useApi/useFetchRailzBills';
import useFetchRailzInvoices from '@/hooks/useApi/useFetchRailzInvoices';
import useFetchRailzAccount from '@/hooks/useApi/useFetchRailzAccount';
import usePostAccountBalance from '@/hooks/useApi/usePostAccountBalance';
import usePostRailzSync from '@/hooks/useApi/usePostRailzSync';
import usePostRailzSyncData from '@/hooks/useApi/useFetchRailzSyncStatus';
import AccountingSystemDialogue from './AccountingSystemDialogue/AccountingSystemDialogue';
import AccountingSystemContext, { AccountingSystemCtx } from './AccountingSystemDialogue/AccountingSystemContext';
import { AccountingSystemDialogueHandler, DialogueHandlerProps } from './AccountingSystemDialogue/types';
import { useClientLogin } from '@/providers/ClientLogin.provider';
import styles from './AccountingSystem.module.scss';
import AvailableBalance from '../../UI/AvailableBalance/AvailableBalance';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import { Trans, useTranslation } from 'react-i18next';
import { TableFiltersContainer, Button, CircleButton } from 'UI';
import { useHistory, useLocation } from 'react-router-dom';
type AccountingType = 'Payable' | 'Receivable';
type Tab = 'open' | 'closed';

type QueryParams = {
  tab: string | null;
  type: string | null;
  currency: string | null;
  searchTerm: string | null;
  timeRange: string | null;
  startDate: string | null;
  endDate: string | null;
  status: string | null;
  paymentChannel: string | null;
  dateFilterType: string | null;
};

type AccountingSystemContainerProps = {
  Type: AccountingType;
};

type FC = FunctionComponent<AccountingSystemContainerProps>;
const AccountingSystemContainer: FC = ({ Type }: AccountingSystemContainerProps) => {
  const [, , fetchRailzBills] = useFetchRailzBills({});
  const [, , fetchRailzInvoices] = useFetchRailzInvoices({});
  const [railzBusinessAccountResponse, fetchRailzBusinessAccount, resetRailzBusinessAccount] =
    useFetchRailzBusinessAccount({});
  const [, , fetchChartOfAccount] = useFetchRailzAccount({});
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [balanceResponse, fetchBalance] = usePostAccountBalance({});
  const [syncDataResponse, syncData, resetSyncData] = usePostRailzSync({});
  const [syncStatusResponse, fetchSyncStatus] = usePostRailzSyncData({});
  const [fetchStatus, setFetchStatus] = useState(true);
  const [isSyncingData, setIsSyncingData] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastSync, setLastSync] = useState('');
  const [businessAccount, setBusinessAccount] = useState<vp.RailzBusinessAccount | undefined>(undefined);
  const [railzBillsOrInvoices, setRailzBillsOrInvoices] = useState<vp.BillOrInvoiceRailz[]>([]);
  const [BillsOrInvoices, setBillsOrInvoices] = useState<vp.BillOrInvoiceRailz[]>([]);
  const [railzBillsOrInvoicesFiltered, setRailzBillsOrInvoicesFiltered] = useState<vp.BillOrInvoiceRailz[]>([]);
  const [BillsOrInvoicesFiltered, setBillsOrInvoicesFiltered] = useState<vp.BillOrInvoiceRailz[]>([]);

  const [showChartOfAccountSuccessfull, setShowChartOfAccountSuccessfull] = useState(false);

  const [chartOfAccountNameRecievable, setChartOfAccountNameRecievable] = useState('');
  const [chartOfAccountNamePayable, setChartOfAccountNamePayable] = useState('');
  const { response } = useClientLogin();

  const history = useHistory();
  const location = useLocation();
  const path = Type === 'Payable' ? 'payables' : 'receivables';
  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  // Helper function to parse query params
  const getQueryParams = () => {
    const params = new URLSearchParams(location.search);
    return {
      tab: (params.get('tab') as Tab) || 'open',
      type: params.get('type') || '',
      currency: (params.get('currency') as Currency) || '',
      searchTerm: params.get('searchTerm') || '',
      timeRange: (params.get('timeRange') as TimeRange) || 'custom',
      startDate: params.get('startDate')
        ? (Number(params.get('startDate')) as vp.Timestamp)
        : (startOfMonth(subMonths(new Date(), 1)).getTime() as vp.Timestamp),
      endDate: params.get('endDate')
        ? (Number(params.get('endDate')) as vp.Timestamp)
        : (endOfMonth(new Date()).getTime() as vp.Timestamp),
      status: (params.get('status') as StatusFilter) || '',
      paymentChannel: (params.get('paymentChannel') as PaymentChannel) || '',
      dateFilterType: (params.get('dateFilterType') as DateFilterType) || 'closedDate',
    };
  };

  // Helper function to calculate date range from time range key
  const calculateDateRange = (
    timeRangeKey: TimeRange,
    startDate: vp.Timestamp | undefined,
    endDate: vp.Timestamp | undefined,
  ): { startDate: Date | undefined; endDate: Date | undefined } => {
    const today = new Date();
    let adjustedStartDate: Date | undefined;
    let adjustedEndDate: Date | undefined;

    switch (timeRangeKey) {
      case 'today':
        adjustedStartDate = today;
        adjustedEndDate = today;
        break;
      case 'yesterday':
        adjustedStartDate = subDays(today, 1);
        adjustedEndDate = adjustedStartDate;
        break;
      case 'thisWeek':
        adjustedStartDate = startOfWeek(today);
        adjustedEndDate = endOfWeek(today);
        break;
      case 'lastWeek':
        adjustedStartDate = startOfWeek(subDays(today, 7));
        adjustedEndDate = endOfWeek(subDays(today, 7));
        break;
      case 'thisMonth':
        adjustedStartDate = startOfMonth(today);
        adjustedEndDate = endOfMonth(today);
        break;
      case 'lastMonth':
        adjustedStartDate = startOfMonth(subMonths(today, 1));
        adjustedEndDate = endOfMonth(subMonths(today, 1));
        break;
      case 'thisQuarter':
        adjustedStartDate = startOfQuarter(today);
        adjustedEndDate = endOfQuarter(today);
        break;
      case 'lastQuarter':
        adjustedStartDate = startOfQuarter(subQuarters(today, 1));
        adjustedEndDate = endOfQuarter(subQuarters(today, 1));
        break;
      case 'last6Months':
        adjustedStartDate = startOfMonth(subMonths(today, 6));
        adjustedEndDate = endOfMonth(today);
        break;
      case 'last12Months':
        adjustedStartDate = undefined;
        adjustedEndDate = undefined;
        break;
      case 'custom':
        // Return dates from URL params for custom
        return { startDate: new Date(Number(startDate)), endDate: new Date(Number(endDate)) };
      default:
        // Return dates from URL params for any other value (shouldn't happen with strict typing)
        return { startDate: new Date(Number(startDate)), endDate: new Date(Number(endDate)) };
    }

    return { startDate: adjustedStartDate, endDate: adjustedEndDate };
  };

  // Derive state from query params using useMemo
  const queryParams = useMemo(() => getQueryParams(), [location.search]);

  const activeTab = useMemo<'open' | 'closed'>(() => queryParams.tab || 'open', [queryParams.tab]);
  const type = useMemo(() => queryParams.type, [queryParams.type]);
  const currency = useMemo(() => queryParams.currency as Currency, [queryParams.currency]);
  const searchTerm = useMemo(() => queryParams.searchTerm, [queryParams.searchTerm]);
  const timeRange = useMemo(() => queryParams.timeRange, [queryParams.timeRange]);
  const status = useMemo(() => queryParams.status as StatusFilter, [queryParams.status]);
  const paymentChannel = useMemo(
    () => (queryParams.paymentChannel || '') as PaymentChannel,
    [queryParams.paymentChannel],
  );
  const dateFilterType = useMemo(() => queryParams.dateFilterType as DateFilterType, [queryParams.dateFilterType]);

  // Calculate dates based on timeRange, but use URL params for custom range
  const { startDate, endDate } = useMemo(() => {
    if (queryParams.timeRange === 'custom') {
      // For custom range, use the dates from URL params
      return {
        startDate: queryParams.startDate,
        endDate: queryParams.endDate,
      };
    } else {
      // For preset ranges, calculate the dates
      const { startDate: calcStart, endDate: calcEnd } = calculateDateRange(
        queryParams.timeRange,
        queryParams.startDate,
        queryParams.endDate,
      );
      return {
        startDate: calcStart?.getTime() as vp.Timestamp | undefined,
        endDate: calcEnd?.getTime() as vp.Timestamp | undefined,
      };
    }
  }, [queryParams.timeRange, queryParams.startDate, queryParams.endDate]);

  const [lastStartDate, setLastStartDate] = useState<vp.Timestamp | undefined>(startDate);
  const [lastEndDate, setLastEndDate] = useState<vp.Timestamp | undefined>(endDate);

  const [showSyncStarted, setShowSyncStarted] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { t } = useTranslation('translations', { keyPrefix: 'AccountingSystem' });
  const { t: tOpenTab } = useTranslation('translations', { keyPrefix: 'AccountingSystem.OpenTab' });

  // State to hold action handlers from AccountingSystemOpen
  const [openTabActions, setOpenTabActions] = useState<{ onScheduleAll: () => void } | null>(null);

  const titleMap: Record<AccountingType, string> = {
    Payable: t('pageTitlePayable'),
    Receivable: t('pageTitleReceivable'),
  };

  const accountingSystemCtx: AccountingSystemCtx = {
    showSyncStarted,
    showError,
    errorMessage,
  };

  const handlers = {
    handleAccountDialogue: (handlerProps: DialogueHandlerProps<AccountingSystemDialogueHandler>) => {
      const { type: typeHandler, state: state2, action = 'default' } = handlerProps;

      const dialogueHandlers: AccountingSystemDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowError(false);
            },
          },
        },
        sync: {
          confirm: {
            default: () => {
              setShowSyncStarted(false);
            },
          },
          fail: {
            default: () => {},
          },
        },
      };
      const dialogueState = dialogueHandlers[typeHandler][state2];
      const handler = dialogueState ? dialogueState[action] || dialogueState.default : () => {};

      handler();
    },
  };

  // Get the protocol, hostname, and port
  const { protocol, port, hostname } = window.location;
  let RedirectURL = `${protocol}//${hostname}/${path}/`;
  const currentPort = port || '3000';

  if (hostname === 'localhost' || hostname === 'bs-local.com') {
    RedirectURL = `${protocol}//${hostname}:${currentPort}/${path}/`;
  }

  useEffect(() => {
    if (syncDataResponse) {
      resetSyncData();
      if (syncStatusResponse?.Success) {
        setShowSyncStarted(true);
      } else {
        setErrorMessage(syncStatusResponse?.ErrorMessage || '');
        setShowError(true);
      }
      setFetchStatus(true);
    }
  }, [syncDataResponse]);
  useEffect(() => {
    if (fetchStatus && businessAccount) {
      fetchSyncStatus({
        BusinessName: businessAccount.BusinessName || '',
        ServiceName: businessAccount.ServiceName || '',
      });
      setFetchStatus(false);
    }
  }, [fetchStatus, businessAccount]);
  useEffect(() => {
    if (syncStatusResponse) {
      if (syncStatusResponse.Success) {
        if (syncStatusResponse.SyncStatus === 'success') {
          setIsSyncingData(false);
        }
        syncStatusResponse.MostRecentUpdateDate &&
          setLastSync(format(parseISO(syncStatusResponse.MostRecentUpdateDate), 'yyyy-MM-dd HH:mm:ss'));
      }
    }
  }, [syncStatusResponse]);
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (isSyncingData) {
      timeoutId = setTimeout(() => {
        setFetchStatus(true);
      }, 60000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isSyncingData]);

  useEffect(() => {
    fetchBalance({
      Currency: 'CAD',
    });
  }, []);

  // Filter function: Check if currency matches
  const filterByCurrency = (data: vp.BillOrInvoiceRailz): boolean => {
    return currency === '' || currency === 'all' || currency.split('|').includes(data.currency);
  };

  // Filter function: Check if status matches (for open tab)
  const filterByStatus = (data: vp.BillOrInvoiceRailz): boolean => {
    return status === '' || status === 'all' || data.status === status;
  };

  // Filter function: Check payment method availability for open items
  const filterByPaymentMethodAvailability = (data: vp.BillOrInvoiceRailz): boolean => {
    if (type === '' || type === 'all') return true;

    const hasDetails = {
      hasBankAccount: !!(
        data.Customer &&
        data.Customer.BankAccount &&
        data.Customer.BankAccount.FinancialInstitutionNumber &&
        data.Customer.BankAccount.BranchTransitNumber &&
        data.Customer.BankAccount.AccountNumber &&
        ((response.Country === 'CA' &&
          ((Type === 'Receivable' && vp.toBool(response.EFTCollectEnabled)) ||
            (Type === 'Payable' && (vp.toBool(response.EFTSendEnabled) || vp.toBool(response.RTRWithdrawEnabled))))) ||
          (response.Country === 'US' &&
            ((Type === 'Receivable' && vp.toBool(response.ACHFundEnabled)) ||
              (Type === 'Payable' &&
                (vp.toBool(response.ACHWithdrawEnabled) || vp.toBool(response.RTPWithdrawEnabled))))))
      ),
      hasInterac: !!(
        data.Customer &&
        data.Customer.Interac &&
        (data.Customer.Interac.RecipientEmailAddress || data.Customer.Interac?.RecipientPhoneNumber) &&
        (data.Customer.Interac.Question || Type === 'Receivable') &&
        (data.Customer.Interac.Answer || Type === 'Receivable') &&
        ((response.Country === 'CA' && Type === 'Receivable' && vp.toBool(response.InteracMoneyRequestEnabled)) ||
          (response.Country === 'CA' && Type === 'Payable' && vp.toBool(response.InteracBulkPayoutEnabled)))
      ),
      hasDebitCard: !!(
        data.Customer &&
        data.Customer.DebitCard &&
        data.Customer.DebitCard.PushToCardToken &&
        vp.toBool(response.VisaDirectEnabled) &&
        Type === 'Payable'
      ),
      hasCreditCard: !!(
        data.Customer &&
        data.Customer.CreditCard &&
        data.Customer.CreditCard.CreditCardToken &&
        vp.toBool(response.CreditCardEnabled) &&
        Type === 'Receivable'
      ),
    };

    return (
      (hasDetails.hasBankAccount &&
        (type.split('|').includes('EFT') ||
          type.split('|').includes('ACH') ||
          type.split('|').includes('VoPay Instant'))) ||
      (hasDetails.hasInterac && type.split('|').includes('Interac e-Transfer')) ||
      (hasDetails.hasDebitCard && type.split('|').includes('Debit Card')) ||
      (hasDetails.hasCreditCard && type.split('|').includes('Credit Card'))
    );
  };

  // Filter function: Check payment method used for closed items
  const filterByPaymentMethodUsed = (data: vp.BillOrInvoiceRailz): boolean => {
    if (type === '' || type === 'all') return true;

    const paymentMethods =
      data.PaymentDetails?.map((payment) =>
        payment.TransactionAmount && Number(payment.TransactionAmount) > 0 ? payment.PaymentMethod : undefined,
      ).filter((payment) => !!payment) || [];

    const selectedTypes = type
      .split('|')
      .map((typeOriginal) =>
        typeOriginal === 'EFT' || typeOriginal === 'ACH' || typeOriginal === 'VoPay Instant'
          ? 'Bank Account'
          : typeOriginal === 'Interac e-Transfer'
            ? 'Email'
            : typeOriginal,
      );

    return paymentMethods.some((method) => method && selectedTypes.includes(method));
  };

  // Filter function: Check if search term matches
  const filterBySearchTerm = (data: vp.BillOrInvoiceRailz, searchKeys: string[]): boolean => {
    if (searchTerm === '') return true;

    const customerName: string = data.Customer?.Name || data.customerRef?.name || data.vendorRef?.name || '';

    return (
      searchKeys.some((key) =>
        data[key as keyof vp.BillOrInvoiceRailz]?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      ) || customerName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Filter function: Check payment channel for closed items
  const filterByPaymentChannel = (data: vp.BillOrInvoiceRailz): boolean => {
    if (!paymentChannel || paymentChannel === 'all') {
      return true;
    }

    // Check if PaymentDetails exists and is an array
    if (!data.PaymentDetails || !Array.isArray(data.PaymentDetails) || data.PaymentDetails.length === 0) {
      // No payment details - treat as ERP payment if looking for ERP
      return paymentChannel === 'erp';
    }

    if (paymentChannel === 'vopay') {
      // VoPay: has transaction IDs
      return data.PaymentDetails.some((payment) => payment.TransactionID && payment.TransactionID !== '');
    } else if (paymentChannel === 'erp') {
      // ERP: paid through accounting software (no transaction IDs)
      const hasTransactionIds = data.PaymentDetails.some(
        (payment) => payment.TransactionID && payment.TransactionID !== '',
      );

      // Also check if it's not a credit-only payment
      const allCredits = data.PaymentDetails.every(
        (payment) =>
          payment.CreditAmount !== null &&
          Number(payment.CreditAmount) > 0 &&
          (Number(payment.TransactionAmount) === 0 || !payment.TransactionAmount),
      );

      return !hasTransactionIds && !allCredits;
    }

    return true;
  };

  const searchTransactions = () => {
    const openItemsSearchKeys = ['invoiceNumber', 'amountDue'];
    const closedItemsSearchKeys = ['InvoiceRefID', 'VIOAccountTransactions_ID', 'Amount'];

    // Filter open bills/invoices
    setRailzBillsOrInvoicesFiltered(
      railzBillsOrInvoices
        .filter((data) => {
          return (
            filterByCurrency(data) &&
            filterByPaymentMethodAvailability(data) &&
            filterBySearchTerm(data, openItemsSearchKeys) &&
            filterByStatus(data)
          );
        })
        .sort((a, b) => compareAsc(parseISO(b.postedDate), parseISO(a.postedDate))),
    );

    // Filter closed bills/invoices
    setBillsOrInvoicesFiltered(
      BillsOrInvoices.filter((data) => {
        return (
          filterByPaymentMethodUsed(data) &&
          filterByCurrency(data) &&
          filterBySearchTerm(data, closedItemsSearchKeys) &&
          filterByPaymentChannel(data)
        );
      }).sort((a, b) => compareAsc(parseISO(b.postedDate), parseISO(a.postedDate))),
    );
  };

  useEffect(() => {
    setBillsOrInvoicesFiltered([...BillsOrInvoices]);
    searchTransactions();
  }, [BillsOrInvoices]);
  useEffect(() => {
    setRailzBillsOrInvoicesFiltered([...railzBillsOrInvoices]);
    searchTransactions();
  }, [railzBillsOrInvoices]);

  // Trigger search when filter params change from URL
  useEffect(() => {
    if (railzBillsOrInvoices.length > 0 || BillsOrInvoices.length > 0) {
      searchTransactions();
    }
  }, [type, currency, searchTerm, status, paymentChannel]);

  useEffect(() => {
    if (railzBusinessAccountResponse && railzBusinessAccountResponse.Success) {
      const hasNoAccount = Object.values(railzBusinessAccountResponse.Accounts).length === 0;
      if (!hasNoAccount) {
        const currentAccount: vp.RailzBusinessAccount | undefined = Object.values(
          railzBusinessAccountResponse.Accounts,
        )[0];
        setBusinessAccount(currentAccount);
      } else {
        setShowConnect(hasNoAccount);
      }

      if (hasNoAccount) {
        setIsInitialized(true);
      }
    }
  }, [railzBusinessAccountResponse]);
  const fetchBillsOrInvoices = async (
    accountingType: AccountingType,
    account: vp.RailzBusinessAccount,
    transactionsPeriod: {
      FromDate: vp.Timestamp | undefined;
      ToDate: vp.Timestamp | undefined;
    },
  ) => {
    setDataFetchingInfo(true, 'loading some data', 'fullScreen');
    try {
      if (accountingType === 'Payable') {
        const [openBills, paidBills] = await Promise.all([
          fetchRailzBills({
            BusinessName: account.BusinessName || '',
            ServiceName: account.ServiceName || '',
            StartDate: format(new Date(Number(transactionsPeriod.FromDate)), 'yyyy-MM-dd'),
            EndDate: format(new Date(Number(transactionsPeriod.ToDate)), 'yyyy-MM-dd'),
            Status: 'open',
          }),
          fetchRailzBills({
            BusinessName: account.BusinessName || '',
            ServiceName: account.ServiceName || '',
            StartDate: format(new Date(Number(transactionsPeriod.FromDate)), 'yyyy-MM-dd'),
            EndDate: format(new Date(Number(transactionsPeriod.ToDate)), 'yyyy-MM-dd'),
            Status: 'paid',
          }),
        ]);
        if (openBills.Success && openBills.Bills) {
          setRailzBillsOrInvoices(openBills.Bills || []);
        } else {
          setRailzBillsOrInvoices([]);
        }
        if (paidBills.Success && paidBills.Bills) {
          setBillsOrInvoices(paidBills.Bills || []);
        } else {
          setBillsOrInvoices([]);
        }
      } else {
        const [openInvoices, paidInvoices] = await Promise.all([
          fetchRailzInvoices({
            BusinessName: account.BusinessName || '',
            ServiceName: account.ServiceName || '',
            StartDate: format(new Date(Number(transactionsPeriod.FromDate)), 'yyyy-MM-dd'),
            EndDate: format(new Date(Number(transactionsPeriod.ToDate)), 'yyyy-MM-dd'),

            Status: 'open',
          }),
          fetchRailzInvoices({
            BusinessName: account.BusinessName || '',
            ServiceName: account.ServiceName || '',
            StartDate: format(new Date(Number(transactionsPeriod.FromDate)), 'yyyy-MM-dd'),
            EndDate: format(new Date(Number(transactionsPeriod.ToDate)), 'yyyy-MM-dd'),
            Status: 'paid',
          }),
        ]);
        if (openInvoices.Success && openInvoices.Invoices) {
          setRailzBillsOrInvoices(openInvoices.Invoices || []);
        } else {
          setRailzBillsOrInvoices([]);
        }
        if (paidInvoices.Success && paidInvoices.Invoices) {
          setBillsOrInvoices(paidInvoices.Invoices || []);
        } else {
          setBillsOrInvoices([]);
        }
      }
    } finally {
      setDataFetchingInfo(false);
    }
  };
  useEffect(() => {
    const fetchCharts = async (BusinessName: string, ServiceName: string) => {
      const chartOfAccountRecievables = await fetchChartOfAccount({
        BusinessName,
        ServiceName,
        Type: 'receivables',
      });
      const chartOfAccountPayables = await fetchChartOfAccount({
        BusinessName,
        ServiceName,
        Type: 'payables',
      });
      if (
        chartOfAccountRecievables.Success &&
        chartOfAccountRecievables.Account &&
        chartOfAccountPayables.Success &&
        chartOfAccountPayables.Account
      ) {
        setShowConnect(false);
      } else {
        setShowConnect(true);
      }
    };

    if (businessAccount) {
      fetchCharts(businessAccount.BusinessName || '', businessAccount.ServiceName || '');
      fetchBillsOrInvoices(Type, businessAccount, {
        FromDate: startDate,
        ToDate: endDate,
      });
      setIsInitialized(true);
    }
  }, [businessAccount]);

  const OnSuccess = () => {
    if (businessAccount) {
      fetchBillsOrInvoices(Type, businessAccount, {
        FromDate: startDate,
        ToDate: endDate,
      });
    }
  };

  useEffect(() => {
    setIsInitialized(false);
    updateQueryParams({
      tab: 'open',
      type: null,
      currency: null,
      searchTerm: null,
      timeRange: null,
      startDate: null,
      endDate: null,
      status: null,
      paymentChannel: null,
      dateFilterType: null,
    });
    setRailzBillsOrInvoices([]);
    setBillsOrInvoices([]);
    resetRailzBusinessAccount();
    fetchRailzBusinessAccount({});
    setFetchStatus(true);
  }, [Type]);

  // Fetch new data when date range changes
  useEffect(() => {
    if (businessAccount && startDate && endDate) {
      // Only fetch if dates have actually changed
      if (lastStartDate !== startDate || lastEndDate !== endDate) {
        fetchBillsOrInvoices(Type, businessAccount, {
          FromDate: startDate,
          ToDate: endDate,
        });
        setLastStartDate(startDate);
        setLastEndDate(endDate);
      }
    }
  }, [startDate, endDate, businessAccount, Type, dateFilterType]);

  const onCompleteChartOfAccount = (accountNameRecievable: string, accountNamePayable: string) => {
    setChartOfAccountNameRecievable(accountNameRecievable);
    setChartOfAccountNamePayable(accountNamePayable);
    setShowChartOfAccountSuccessfull(true);
    setShowConnect(false);
  };

  const handleTabChange = (value: string) => {
    if (value === 'open' || value === 'closed') {
      updateQueryParams({ tab: value });
    }
  };

  const handleSearch = (params: SearchParams) => {
    // Update URL query params with all search parameters
    // The useEffects will handle fetching data and filtering based on the new URL params
    updateQueryParams({
      type: params.type || null,
      currency: params.currency || null,
      searchTerm: params.searchTerm || null,
      timeRange: params.timeRange || null,
      startDate: params.startDate?.toString() || null,
      endDate: params.endDate?.toString() || null,
      status: params.status || null,
      paymentChannel: params.paymentChannel || null,
      dateFilterType: params.dateFilterType || null,
    });
  };

  return (
    <AccountingSystemContext.Provider value={accountingSystemCtx}>
      {isInitialized && (
        <>
          {!showConnect && (
            <>
              <Box flx="row" jc="sb" ai="fs">
                <Box flx="col">
                  <Title title={titleMap[Type]} />

                  <Box flx="col" width="100%" mt="xmd" mb="xmd" base="8">
                    <p>{t('pageSubTitle')}</p>
                  </Box>
                </Box>
                <Box>
                  <AvailableBalance availableBalance={balanceResponse.AvailableFunds} />
                </Box>
              </Box>
              <Box jc="sb" ai="center">
                <Box ai="center" gap="sm">
                  <Box gap="sm">
                    {(syncStatusResponse?.SyncStatus === 'success' || syncStatusResponse?.SyncStatus === 'empty') && (
                      <Box>{t('lastSync', { lastSync })}</Box>
                    )}
                    {syncStatusResponse?.SyncStatus === 'pending' && (
                      <Box>
                        <Trans
                          ns="translations"
                          i18nKey="AccountingSystem.SyncStatus.label"
                          components={{
                            span: <span className={styles.syncHighlight} />,
                          }}
                        />
                        <div className={styles.tooltipContianer}>
                          <Tooltip icon="question" information={t('SyncStatus.tooltip')} position="topLeft" />
                        </div>
                      </Box>
                    )}
                    {syncStatusResponse?.SyncStatus === 'failed' && (
                      <Box>
                        <div className={styles.tooltipContianerError}>
                          <Tooltip
                            customElement={<Icon icon="warning" color="error" height="17px" width="17px" />}
                            hasButton={false}
                            position="top"
                            information={t('SyncStatusFail.tooltip')}
                          />
                        </div>
                        <Trans
                          ns="translations"
                          i18nKey="AccountingSystem.SyncStatusFail.label"
                          values={{ lastSync }}
                          components={{
                            span: <span className={[styles.syncHighlight, styles.error].join(' ')} />,
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                  <CircleButton
                    id="Sync-Data"
                    color="secondary"
                    disabled={isSyncingData}
                    type="button"
                    icon="cached"
                    colorScheme="neutral"
                    size="large"
                    onClick={() => {
                      if (!isSyncingData && businessAccount) {
                        setIsSyncingData(true);
                        syncData({
                          BusinessName: businessAccount.BusinessName || '',
                          ServiceName: businessAccount.ServiceName || '',
                        });
                      }
                    }}
                  />
                </Box>
                {activeTab === 'open' && openTabActions && (
                  <Box flx="row" gap="sm">
                    <Button
                      id="collect-on-due-date"
                      colorScheme="secondary"
                      type="button"
                      onClick={() => {
                        openTabActions.onScheduleAll();
                      }}
                    >
                      {Type === 'Receivable' ? tOpenTab('collectAllButton') : tOpenTab('payAllButton')}
                    </Button>
                    <Button
                      id="create"
                      colorScheme="primary"
                      type="button"
                      onClick={() => {
                        if (Type === 'Receivable') {
                          history.push('/receivables/create-invoice');
                        } else {
                          history.push('/payables/create-bill');
                        }
                      }}
                    >
                      {Type === 'Receivable' ? tOpenTab('createInvoiceButton') : tOpenTab('createBillButton')}
                    </Button>
                  </Box>
                )}
              </Box>

              <Box flx="col" width="100%" mt="lg">
                <CardTabs.Root value={activeTab} onValueChange={handleTabChange}>
                  <CardTabs.List aria-label="Accounting Software">
                    <CardTabs.Trigger value="open">{t('openTab', { Type })}</CardTabs.Trigger>
                    <CardTabs.Trigger value="closed">{t('closedTab', { Type })}</CardTabs.Trigger>
                  </CardTabs.List>

                  <CardTabs.Content value="open">
                    <Box flx="col" gap="sm">
                      <TableFiltersContainer filtersApplied>
                        <TableFiltersContainer.Trigger />
                        <TableFiltersContainer.Content>
                          <AccountingSystemSearchForm
                            section={Type}
                            country={response.Country}
                            type={type}
                            timeRange={timeRange}
                            startDate={startDate}
                            endDate={endDate}
                            currency={currency}
                            searchTerm={searchTerm}
                            status={status}
                            paymentChannel={paymentChannel}
                            dateFilterType={dateFilterType}
                            activeTab={activeTab}
                            onSearch={handleSearch}
                          />
                        </TableFiltersContainer.Content>
                      </TableFiltersContainer>
                    </Box>
                    <AccountingSystemOpen
                      OnSuccess={OnSuccess}
                      BillsOrInvoices={railzBillsOrInvoicesFiltered}
                      BusinessName={businessAccount?.BusinessName || ''}
                      ServiceName={businessAccount?.ServiceName || ''}
                      Type={Type}
                      AccountBalance={balanceResponse}
                      onActionsReady={(actions) => setOpenTabActions(actions)}
                    />
                  </CardTabs.Content>

                  <CardTabs.Content value="closed">
                    <Box flx="col" gap="sm">
                      <TableFiltersContainer filtersApplied>
                        <TableFiltersContainer.Trigger />
                        <TableFiltersContainer.Content>
                          <AccountingSystemSearchForm
                            section={Type}
                            country={response.Country}
                            type={type}
                            timeRange={timeRange}
                            startDate={startDate}
                            endDate={endDate}
                            currency={currency}
                            searchTerm={searchTerm}
                            status={status}
                            paymentChannel={paymentChannel}
                            dateFilterType={dateFilterType}
                            activeTab={activeTab}
                            onSearch={handleSearch}
                          />
                        </TableFiltersContainer.Content>
                      </TableFiltersContainer>
                    </Box>
                    <AccountingSystemClosed
                      ServiceName={businessAccount?.ServiceName || ''}
                      Type={Type}
                      BillsOrInvoices={BillsOrInvoicesFiltered}
                    />
                  </CardTabs.Content>
                </CardTabs.Root>
              </Box>
              <Toast
                label={
                  <Trans
                    ns="translations"
                    i18nKey="AccountingSystem.showChartOfAccountSuccessfulToast"
                    values={{ chartOfAccountNameRecievable, chartOfAccountNamePayable }}
                    components={{
                      span: <span style={{ fontWeight: 700 }} />,
                    }}
                  />
                }
                isVisible={showChartOfAccountSuccessfull}
              />
            </>
          )}
          {showConnect && (
            <AccountingSoftware redirectURL={RedirectURL} onCompleteChartOfAccount={onCompleteChartOfAccount} />
          )}
        </>
      )}
      <AccountingSystemDialogue handleDialogue={handlers.handleAccountDialogue} />
    </AccountingSystemContext.Provider>
  );
};
export default AccountingSystemContainer;
